import React, { createContext, useState, useCallback, useEffect, useContext } from 'react';
import { billingApi, type CreditsSummary } from '../api';
import { AuthContext } from './AuthContext';

interface CreditsContextType {
  credits: CreditsSummary | null;
  isLoading: boolean;
  error: Error | null;
  refreshCredits: () => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const CreditsContext = createContext<CreditsContextType | undefined>(undefined);

interface CreditsProviderProps {
  children: React.ReactNode;
}

export const CreditsProvider: React.FC<CreditsProviderProps> = ({ children }) => {
  const auth = useContext(AuthContext);
  const [credits, setCredits] = useState<CreditsSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refreshCredits = useCallback(async () => {
    // Don't fetch if not authenticated
    if (!auth?.isAuthenticated) {
      setCredits(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    try {
      setIsLoading(true);
      const data = await billingApi.getMyCredits();
      setCredits(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch credits'));
    } finally {
      setIsLoading(false);
    }
  }, [auth?.isAuthenticated]);

  // Fetch credits when authentication state changes
  useEffect(() => {
    // Wait until auth has finished loading before fetching credits
    if (auth?.isLoading) {
      return;
    }
    refreshCredits();
  }, [auth?.isLoading, auth?.isAuthenticated, refreshCredits]);

  return (
    <CreditsContext.Provider value={{ credits, isLoading, error, refreshCredits }}>
      {children}
    </CreditsContext.Provider>
  );
};
